import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { andFilters, links, useListSearch } from '@eis/core';
import {
  PageHeader,
  PageLayout,
  PropertyFilter,
  RequirePermission,
  cardSurfaceSx,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList } from '../api/leads';
import { ImportLeadsWizard } from './ImportLeadsWizard';
import { LeadGrid } from './LeadGrid';

const MEMBERSHIPS = ['STANDALONE', 'MEMBER', 'ALL'] as const;
type Membership = (typeof MEMBERSHIPS)[number];

/**
 * Leads — the operations persona's list (SDD-013 §8.1.1, R-1; SDD-017 §9).
 *
 * The default answers "my normal leads": `membership = STANDALONE`. The "Zugehörigkeit" choice
 * widens it to members or everything; a member row shows its agreement as a link (R-2 from the
 * other direction). Everything else — status kind, tarif, dates, names — is the property filter
 * over the lead catalog, plus the standard search; the service filters, orders and pages, and
 * the URL holds the whole state.
 */
export function LeadListPage(): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const url = useListSearch();
  const membership: Membership = MEMBERSHIPS.includes(url.raw.membership as Membership)
    ? (url.raw.membership as Membership)
    : 'STANDALONE';

  const [importing, setImporting] = useState(false);
  const catalog = useFilterCatalog('leads');
  const list = useInfiniteList('leads', {
    ...url.params,
    filters: andFilters(
      membership === 'ALL' ? undefined : `membership==${membership}`,
      url.params.filters,
    ),
  });
  const filtered =
    membership !== 'STANDALONE' ||
    (url.params.filters ?? '') !== '' ||
    (url.params.search ?? '') !== '';

  const header = (
    <PageHeader
      title={t('leads.title')}
      description={t('leads.subtitle')}
      actions={
        <RequirePermission anyOf={[p.leads.create]}>
          <Button
            variant="contained"
            onClick={() => {
              void navigate({ href: `${links.insurance.leads()}/new` });
            }}
          >
            {t('leads.actions.newLead')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setImporting(true);
            }}
          >
            {t('leads.actions.import')}
          </Button>
          <ImportLeadsWizard
            open={importing}
            onClose={() => {
              setImporting(false);
              list.refresh();
            }}
          />
        </RequirePermission>
      }
    />
  );

  return (
    <PageLayout header={header}>
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            <TextField
              select
              size="small"
              label={t('leads.filters.membership')}
              value={membership}
              onChange={(event) => {
                url.set({
                  membership: event.target.value === 'STANDALONE' ? undefined : event.target.value,
                });
              }}
              slotProps={{
                select: { SelectDisplayProps: { 'aria-label': t('leads.filters.membership') } },
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="STANDALONE">{t('leads.filters.standalone')}</MenuItem>
              <MenuItem value="MEMBER">{t('leads.filters.member')}</MenuItem>
              <MenuItem value="ALL">{t('leads.filters.all')}</MenuItem>
            </TextField>
          </Stack>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: url.params.search ?? '' }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
            placeholder={t('leads.list.search')}
          />
          <LeadGrid
            leads={list.items}
            list={list}
            emptyMessage={filtered ? t('leads.list.emptyFiltered') : t('leads.list.empty')}
            showParent
            exportAs={{
              label: t('leads.title'),
              filters: andFilters(
                membership === 'ALL' ? undefined : `membership==${membership}`,
                url.params.filters,
              ),
              search: url.params.search,
              order: url.params.order,
            }}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
          />
        </Stack>
      </Paper>
    </PageLayout>
  );
}
